import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import head from 'lodash/fp/head';

import { ActionBarOverlay } from './ActionBarOverlay';
import { ActionBarItemPopover } from './ActionBarItemPopover';
import { ActionBarItemIcon } from './ActionBarItemIcon';
import OverlayTrigger from '../overlay/OverlayTrigger';
import Dialog from '../dialog/Dialog';

function checkIfSmallResolution() {
    const [isSmallResolution, setIsSmallResolution] = useState(false);
    // We only execute this once. Before it was re rendered on every change.
    // Users probably don't switch between display sizes that often therefore it probably makes sense to
    // avoid the re rendering. Or applying an event listnener.
    useEffect(() => {
        const header = head(document.getElementsByClassName('ApplicationHeader'));
        setIsSmallResolution(header && header.offsetWidth < 580);
    }, []);

    return isSmallResolution;
}

export function ActionBarItem(props) {
    const { className, children, popoverWidth, id, hidePopoverOnClick } = props;
    const [isShown, setIsShown] = useState(false);
    const classes = classNames('ActionBarItem', className);
    const childrenAsList = React.Children.toArray(children);
    const isSmallScreen = checkIfSmallResolution();

    const onHide = useCallback(() => {
        if (hidePopoverOnClick) {
            setIsShown(false);
        }
    }, [hidePopoverOnClick, setIsShown]);

    const onToggle = useCallback(() => {
        setIsShown(!isShown);
    }, [setIsShown, isShown]);

    if (childrenAsList.length === 2) {
        const trigger = childrenAsList[0];
        const popover = childrenAsList[1];

        if (!popover.type.displayName === 'ActionBarItemPopOver') {
            throw Error('The second child should be a ActionBarItemPopover');
        }

        const popoverProps = popover.props;
        const { useOffscreen = false, title } = popoverProps;

        if (!isSmallScreen) {
            const overlay = (
                <ActionBarOverlay
                    id={id}
                    key='child'
                    title={title}
                    width={popoverWidth}
                    preRender={useOffscreen}
                    show={isShown}
                >
                    {popover}
                </ActionBarOverlay>
            );

            return <div className={classes}>
                <OverlayTrigger
                    onToggle={onToggle}
                    show={isShown || useOffscreen}
                    placement={OverlayTrigger.BOTTOM_END}
                    overlay={overlay} trigger={'click'}>
                    {trigger}
                </OverlayTrigger>
            </div>;
        }

        return (
            <div className={classes}>
                {React.cloneElement(trigger, { onClick: onToggle })}
                <Dialog show={isShown} onHide={onHide} body={popover} title={title} />
            </div>
        );
    }

    return <div className={classes}>{children}</div>;
}

ActionBarItem.Icon = ActionBarItemIcon;
ActionBarItem.Popover = ActionBarItemPopover;

ActionBarItem.defaultProps = {
    hidePopoverOnClick: true,
    popoverWidth: 250,
};

ActionBarItem.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    className: PropTypes.string,
    id: PropTypes.string.isRequired,
    hidePopoverOnClick: PropTypes.bool,
    popoverWidth: PropTypes.oneOf([100, 150, 200, 250, 300, 350, 400, 450, 500]),
};

export default ActionBarItem;
